import { apiClient } from "@/services/api-client";
import type {
  AdaptedSignal,
  AgentExecution,
  AgentExecutionPath,
  AgentOrder,
  AgentOrderSubmitRequest,
  AgentOrderSubmitResponse,
  BrokerHealth,
  ExecutionAnalytics,
  ExecutionConfig,
  ExecutionDecision,
  ExecutionHistoryFilters,
  ExecutionHistoryResponse,
  ExecutionMethod,
  ExecutionOverview,
  ExecutionScreenshot,
  // PRD-08 types
  MarketConfig,
  OrderModifyRequest,
  Platform,
  SignalData,
} from "../types/execution.types";

class ExecutionService {
  private baseUrl = "/api/execution";

  // Status API Methods
  async getOverview(): Promise<ExecutionOverview> {
    const response = await apiClient.getWithSuccess<ExecutionOverview>(
      `${this.baseUrl}/status/overview/`,
    );
    if (!response.success) {
      throw new Error(response.error || "Failed to fetch execution overview");
    }
    return response.data!;
  }

  async getQueueStatus() {
    const response = await apiClient.getWithSuccess(
      `${this.baseUrl}/status/queue/`,
    );
    if (!response.success) {
      throw new Error(response.error || "Failed to fetch queue status");
    }
    return response.data;
  }

  async getAvailableMethods() {
    const response = await apiClient.getWithSuccess(
      `${this.baseUrl}/status/methods/`,
    );
    if (!response.success) {
      throw new Error(response.error || "Failed to fetch available methods");
    }
    return response.data;
  }

  // Configuration API Methods
  async getCurrentConfig(): Promise<ExecutionConfig> {
    const response = await apiClient.getWithSuccess<ExecutionConfig>(
      `${this.baseUrl}/config/current/`,
    );
    if (!response.success) {
      throw new Error(response.error || "Failed to fetch current config");
    }
    return response.data!;
  }

  async updateConfig(
    config: Partial<ExecutionConfig>,
  ): Promise<ExecutionConfig> {
    const response = await apiClient.postWithSuccess<{
      status: string;
      message: string;
      config: ExecutionConfig;
    }>(`${this.baseUrl}/config/update_config/`, config);

    if (!response.success) {
      throw new Error(response.error || "Failed to update config");
    }

    return response.data!.config;
  }

  async getPlatforms(): Promise<Platform[]> {
    const response = await apiClient.getWithSuccess<{ platforms: Platform[] }>(
      `${this.baseUrl}/config/platforms/`,
    );
    if (!response.success) {
      throw new Error(response.error || "Failed to fetch platforms");
    }
    return response.data!.platforms;
  }

  async testPlatform(platformId: string) {
    const response = await apiClient.postWithSuccess(
      `${this.baseUrl}/config/test-platform/`,
      {
        platform_id: platformId,
      },
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to test platform");
    }

    return response.data;
  }

  // Analytics API Methods
  async getAnalyticsSummary(
    period: "1h" | "24h" | "7d" | "30d" = "24h",
  ): Promise<ExecutionAnalytics> {
    const response = await apiClient.getWithSuccess<ExecutionAnalytics>(
      `${this.baseUrl}/analytics/summary/`,
      {
        params: { period },
      },
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch analytics summary");
    }

    return response.data!;
  }

  async getAnalyticsTrends(period: "1h" | "24h" | "7d" | "30d" = "7d") {
    const response = await apiClient.getWithSuccess(
      `${this.baseUrl}/analytics/trends/`,
      {
        params: { period },
      },
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch analytics trends");
    }

    return response.data;
  }

  async getMethodComparison(period: "1h" | "24h" | "7d" | "30d" = "7d") {
    const response = await apiClient.getWithSuccess(
      `${this.baseUrl}/analytics/comparison/`,
      {
        params: { period },
      },
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch method comparison");
    }

    return response.data;
  }

  async getSlippageAnalysis() {
    const response = await apiClient.getWithSuccess(
      `${this.baseUrl}/analytics/slippage/`,
    );
    if (!response.success) {
      throw new Error(response.error || "Failed to fetch slippage analysis");
    }
    return response.data;
  }

  // History API Methods
  async getExecutionHistory(
    filters?: ExecutionHistoryFilters,
  ): Promise<ExecutionHistoryResponse> {
    const params: Record<string, any> = {
      page: filters?.page || 1,
      page_size: filters?.page_size || 20,
    };

    if (filters?.method) params.method = filters.method;
    if (filters?.status) params.status = filters.status;
    if (filters?.start_date) params.start_date = filters.start_date;
    if (filters?.end_date) params.end_date = filters.end_date;
    if (filters?.search) params.search = filters.search;

    const response = await apiClient.getWithSuccess<ExecutionHistoryResponse>(
      `${this.baseUrl}/history/`,
      { params },
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch execution history");
    }

    return response.data!;
  }

  async getExecutionDetails(executionId: string) {
    const response = await apiClient.getWithSuccess(
      `${this.baseUrl}/history/${executionId}/`,
    );
    if (!response.success) {
      throw new Error(response.error || "Failed to fetch execution details");
    }
    return response.data;
  }

  async getExecutionScreenshots(
    executionId: string,
  ): Promise<ExecutionScreenshot[]> {
    const response = await apiClient.getWithSuccess<{
      execution_id: string;
      screenshot_count: number;
      screenshots: ExecutionScreenshot[];
    }>(`${this.baseUrl}/history/${executionId}/screenshots/`);

    if (!response.success) {
      throw new Error(
        response.error || "Failed to fetch execution screenshots",
      );
    }

    return response.data!.screenshots;
  }

  async getScreenshotDownloadUrl(
    executionId: string,
    step: number,
  ): Promise<string> {
    const response = await apiClient.getWithSuccess<{
      download_url: string;
      expires_at: string;
    }>(`${this.baseUrl}/history/download-screenshot/`, {
      params: {
        execution_id: executionId,
        step: step,
      },
    });

    if (!response.success) {
      throw new Error(
        response.error || "Failed to get screenshot download URL",
      );
    }

    return response.data!.download_url;
  }

  // Utility Methods
  getExecutionMethodDisplay(method: ExecutionMethod): string {
    const methodLabels = {
      api: "API Execution",
      browser: "Browser Automation",
      hybrid: "Hybrid Mode",
      auto: "Automatic Selection",
    };
    return methodLabels[method] || method;
  }

  getExecutionMethodDescription(method: ExecutionMethod): string {
    const descriptions = {
      api: "Direct API integration with broker platforms",
      browser: "Visual automation through E2B browser sandboxes",
      hybrid: "Combination of API and browser automation",
      auto: "Automatic selection based on conditions",
    };
    return descriptions[method] || "";
  }

  formatLatency(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  }

  formatSuccessRate(rate: number): string {
    return `${(rate * 100).toFixed(1)}%`;
  }

  formatExecutionTime(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  }

  // ==========================================================================
  // PRD-08: Agent Order Execution Methods
  // ==========================================================================

  private agentBaseUrl = "/api";

  // Agent Order Methods
  async submitAgentOrder(
    request: AgentOrderSubmitRequest,
  ): Promise<AgentOrderSubmitResponse> {
    const response = await apiClient.postWithSuccess<AgentOrderSubmitResponse>(
      `${this.agentBaseUrl}/agent-orders/submit/`,
      request,
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to submit agent order");
    }

    return response.data!;
  }

  async getAgentOrders(params?: {
    page?: number;
    page_size?: number;
    status?: string;
  }): Promise<{ count: number; results: AgentOrder[] }> {
    const response = await apiClient.getWithSuccess<{
      count: number;
      results: AgentOrder[];
    }>(`${this.agentBaseUrl}/agent-orders/`, { params });

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch agent orders");
    }

    return response.data!;
  }

  async getAgentOrder(orderId: number): Promise<AgentOrder> {
    const response = await apiClient.getWithSuccess<AgentOrder>(
      `${this.agentBaseUrl}/agent-orders/${orderId}/`,
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch agent order");
    }

    return response.data!;
  }

  async cancelAgentOrder(
    orderId: number,
    reason?: string,
  ): Promise<AgentOrder> {
    const response = await apiClient.postWithSuccess<AgentOrder>(
      `${this.agentBaseUrl}/agent-orders/${orderId}/cancel/`,
      { reason },
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to cancel agent order");
    }

    return response.data!;
  }

  async modifyAgentOrder(
    orderId: number,
    modifications: OrderModifyRequest,
  ): Promise<AgentOrder> {
    const response = await apiClient.patchWithSuccess<AgentOrder>(
      `${this.agentBaseUrl}/agent-orders/${orderId}/modify/`,
      modifications,
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to modify agent order");
    }

    return response.data!;
  }

  // Execution Methods
  async getAgentExecutions(params?: {
    page?: number;
    page_size?: number;
  }): Promise<{ count: number; results: AgentExecution[] }> {
    const response = await apiClient.getWithSuccess<{
      count: number;
      results: AgentExecution[];
    }>(`${this.agentBaseUrl}/executions/`, { params });

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch executions");
    }

    return response.data!;
  }

  async getAgentExecution(executionId: string): Promise<AgentExecution> {
    const response = await apiClient.getWithSuccess<AgentExecution>(
      `${this.agentBaseUrl}/executions/${executionId}/`,
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch execution");
    }

    return response.data!;
  }

  async getExecutionOrders(executionId: string): Promise<AgentOrder[]> {
    const response = await apiClient.getWithSuccess<AgentOrder[]>(
      `${this.agentBaseUrl}/executions/${executionId}/orders/`,
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch execution orders");
    }

    return response.data!;
  }

  // Broker Health Methods
  async getBrokerHealth(broker: string = "tradovate"): Promise<BrokerHealth> {
    const response = await apiClient.getWithSuccess<BrokerHealth>(
      `${this.agentBaseUrl}/broker-health/`,
      { params: { broker } },
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch broker health");
    }

    return response.data!;
  }

  async getExecutionPath(
    broker: string = "tradovate",
  ): Promise<ExecutionDecision> {
    const response = await apiClient.getWithSuccess<ExecutionDecision>(
      `${this.agentBaseUrl}/broker-health/execution-path/`,
      { params: { broker } },
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch execution path");
    }

    return response.data!;
  }

  async invalidateBrokerHealthCache(
    broker: string = "tradovate",
  ): Promise<void> {
    const response = await apiClient.postWithSuccess(
      `${this.agentBaseUrl}/broker-health/invalidate/`,
      { broker },
    );

    if (!response.success) {
      throw new Error(
        response.error || "Failed to invalidate broker health cache",
      );
    }
  }

  // Market Config Methods
  async getMarketConfigs(): Promise<MarketConfig[]> {
    const response = await apiClient.getWithSuccess<MarketConfig[]>(
      `${this.agentBaseUrl}/market-configs/`,
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch market configs");
    }

    return response.data!;
  }

  async getMarketConfig(symbol: string): Promise<MarketConfig> {
    const response = await apiClient.getWithSuccess<MarketConfig>(
      `${this.agentBaseUrl}/market-configs/${symbol}/`,
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to fetch market config");
    }

    return response.data!;
  }

  async adaptSignal(signalData: SignalData): Promise<AdaptedSignal> {
    const response = await apiClient.postWithSuccess<AdaptedSignal>(
      `${this.agentBaseUrl}/market-configs/adapt-signal/`,
      signalData,
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to adapt signal");
    }

    return response.data!;
  }

  // Utility Methods for PRD-08
  getExecutionPathDisplay(path: AgentExecutionPath): string {
    const pathLabels: Record<AgentExecutionPath, string> = {
      api: "Broker API",
      browser: "Browser Automation",
      manual_alert: "Manual Alert",
    };
    return pathLabels[path] || path;
  }

  getExecutionPathIcon(path: AgentExecutionPath): string {
    const pathIcons: Record<AgentExecutionPath, string> = {
      api: "⚡",
      browser: "🌐",
      manual_alert: "⚠️",
    };
    return pathIcons[path] || "📋";
  }

  formatRiskReward(ratio: string | null): string {
    if (!ratio) return "N/A";
    const numRatio = parseFloat(ratio);
    return `1:${numRatio.toFixed(2)}`;
  }

  formatMargin(margin: string): string {
    const numMargin = parseFloat(margin);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(numMargin);
  }
}

export const executionService = new ExecutionService();
export default executionService;
